import algoliasearch from 'algoliasearch';
import aa from 'search-insights';
import { getAlgoliaAPIKey } from '@/api/pathways'

export class AlgoliaClient {
  constructor({ appID, featureName, indices, newKeyCallbacks }) {
    this.appID = appID
    this.featureName = featureName
    this.indices = indices
    this.placeholderUserToken = ''
    this.newKeyCallbacks = newKeyCallbacks || []
  }

  static async create(...args) {
    const algoliaClient = new AlgoliaClient(...args)
    await algoliaClient.init()
    return algoliaClient
  }

  async init() {
    await this.refreshIndex()
    setTimeout(this.checkApiKeyValidityAndWaitRecheck.bind(this), 10000)
  }

  async checkApiKeyValidityAndWaitRecheck() {
    setTimeout(this.checkApiKeyValidityAndWaitRecheck.bind(this), 10000)
    await this.checkApiKeyValidity()
  }


  async search(...args) {
    await this.checkApiKeyValidity()
    try {
      return await this.index.search(...args);
    } catch (error) {
      await this.refreshIndex()
      return this.index.search(...args)
    }
  }

  async multipleQueries(queries, requestOptions) {
    await this.checkApiKeyValidity()
    try {
      return await this.client.multipleQueries(queries, requestOptions)
    } catch (error) {
      await this.refreshIndex()
      return this.client.multipleQueries(queries, requestOptions)
    }
  }


  async checkApiKeyValidity() {
    if (this.shouldRefresh()) {
      await this.refreshIndex()
    }
  }

  shouldRefresh() {
    if (!this.client || !this.apiKey || !this.expiration) {
      return true
    }
    const oneMinuteBuffer = 60 * 1000;
    const validityRemaining = this.expiration - new Date()
    return validityRemaining <= oneMinuteBuffer
  }

  async refreshIndex() {
    let retries = 3;
    while (retries > 0) {
      try {
        return await this.attemptIndexRefresh()
      } catch (e) {
        retries--;
        if (!retries) {
          throw new Error(`Error fetching new temporary Algolia API Key: ${e.toString()}. Retries exhausted.`)
        }
      }
    }
  }

  async attemptIndexRefresh() {
    await this.fetchAPIKey()
    this.client = algoliasearch(this.appID, this.apiKey);
    const indexName = this.getIndexName()
    this.index = this.client.initIndex(indexName)
    for (const callback of this.newKeyCallbacks) {
      callback(this.apiKey)
    }
    return this.index
  }

  getIndexName() {
    return Array.isArray(this.indices) ? this.indices[0] : this.indices
  }

  async fetchAPIKey() {
    const { key, expiration} = await getAlgoliaAPIKey(this.featureName, this.indices)
    this.apiKey = key
    this.expiration = new Date(expiration)
    return this.apiKey
  }

  async initAnalytics() {
    aa('init', {
      appId: this.appID,
      apiKey: await this.getAPIKey(),
      useCookie: true // needed so we can generate a userToken for algolia insights events when user is not logged in
    })

    aa('getUserToken', null, (err, newUserToken) => {
      if (err) {
        console.error(err);
        return;
      }
      this.placeholderUserToken = newUserToken
    });
  }

  async getAPIKey() {
    await this.checkApiKeyValidity()
    return this.apiKey
  }

  getPlaceholderUserToken() {
    return this.placeholderUserToken
  }

}
